import { type StateCreator, create } from "zustand";
import type {
  CollapsedStateSlice,
  ToolbarStatesSlice,
  HighlightingStatesSlice,
  StudioAPIGlobalStore,
  DrawerErrorStateSlice,
  LayoutStateSlice,
  AppStateSlice
} from "./studioFEAPI.store.types";
import {
  TemplateImportStatusTypes,
  ToolTypes
} from "./studioFEAPI.store.types";
import { isEqual } from "lodash-es";

const sliceResetFns = new Set<() => void>();
export const resetFEStore = () => {
  sliceResetFns.forEach(resetFn => {
    resetFn();
  });
};

const collapsedSliceInitialState = {
  collapsedNodes: []
};
const collapsedStateSlice: StateCreator<CollapsedStateSlice> = (set, get) => {
  sliceResetFns.add(() => set(collapsedSliceInitialState));
  return {
    ...collapsedSliceInitialState,
    // TODO: setCollapsedNodes was used in both set(value) and set(cb) way
    // Enforcing cb way for now
    setCollapsedNodes: callback => {
      set(prev => ({ collapsedNodes: callback(prev.collapsedNodes) }));
    },
    setNodeCollapseState: (id, collapsedState) => {
      const collapsedNodes = get().collapsedNodes;
      if (collapsedState) {
        const collapsedNodesSet = new Set([...collapsedNodes, id]);
        set({ collapsedNodes: Array.from(collapsedNodesSet) });
      } else {
        set({
          collapsedNodes: collapsedNodes.filter(
            collapsedNode => collapsedNode !== id
          )
        });
      }
    }
  };
};

const toolbarSliceInitialState = {
  toolbarSearchText: "",
  selectedTool: ToolTypes.NULL,
  templateUID: null
};
const toolbarStateSlice: StateCreator<ToolbarStatesSlice> = set => {
  sliceResetFns.add(() => set(toolbarSliceInitialState));
  return {
    ...toolbarSliceInitialState,
    setToolbarSearchText: searchText => set({ toolbarSearchText: searchText }),
    setSelectedTool: newTool => {
      set({ selectedTool: newTool });
      // Clear search text when closing toolbar
      if (newTool === null) {
        set({ toolbarSearchText: "" });
      }
    },
    templateImportStatus: TemplateImportStatusTypes.NOT_STARTED,
    setTemplateImportStatus: importStatus =>
      set({ templateImportStatus: importStatus }),
    setTemplateUID: templateUID => set({ templateUID })
  };
};

const highlightingStatesSliceInitialState = {
  componentsToHighlight: {
    updatedComponentTags: [],
    addedComponentTags: [],
    deletedComponentTags: [],
    hoveredComponent: null
  },
  selectedWebGLNodeId: ""
};
const highlightingStatesSlice: StateCreator<HighlightingStatesSlice> = set => {
  sliceResetFns.add(() => set(highlightingStatesSliceInitialState));
  return {
    ...highlightingStatesSliceInitialState,
    // TODO: Update this, currently we are making it backwards compatible
    // as this function is called using React way of updating state using cb
    setComponentsToHighlight: callback => {
      set(prev => ({
        componentsToHighlight: callback(prev.componentsToHighlight)
      }));
    },
    setHoveredComponent: componentId => {
      set(prev => ({
        componentsToHighlight: {
          ...prev.componentsToHighlight,
          hoveredComponent: componentId
        }
      }));

      // Callback to clear the hovered component that was set
      return () => {
        set(prev => ({
          componentsToHighlight: {
            ...prev.componentsToHighlight,
            hoveredComponent:
              prev.componentsToHighlight.hoveredComponent === componentId
                ? null
                : prev.componentsToHighlight.hoveredComponent
          }
        }));
      };
    }
  };
};

const drawerErrorSliceInitialState = {
  rulesInEditMode: [],
  formErrors: {
    errors: {},
    schema: { fields: [] }
  },
  isECADrawerSaving: false
};

const propertiesPaneFormSlice: StateCreator<DrawerErrorStateSlice> = (
  set,
  get
) => {
  sliceResetFns.add(() => set(drawerErrorSliceInitialState));
  return {
    ...drawerErrorSliceInitialState,
    setRulesInEditMode: rulesInEditMode =>
      set(prev => ({
        rulesInEditMode:
          typeof rulesInEditMode === "function"
            ? rulesInEditMode(prev.rulesInEditMode)
            : rulesInEditMode
      })),
    setFormErrors: errors => {
      set(prev => {
        if (isEqual(prev.formErrors, errors)) {
          return prev;
        }
        return { formErrors: errors };
      });
    },
    setIsECADrawerSaving: isSaving => set({ isECADrawerSaving: isSaving })
  };
};

const layoutStateSliceInitialState = {
  rightPaneWidth: window.innerWidth * 0.3,
  secondaryPaneWidth: window.innerWidth * 0.3,
  activePaneDefaultState: {
    type: null
  } as const,
  activePane: {
    type: null
  } as const
};
const layoutStateSlice: StateCreator<LayoutStateSlice> = (set, get) => {
  return {
    ...layoutStateSliceInitialState,
    setRightPaneWidth: width => set({ rightPaneWidth: width }),
    setSecondaryPaneWidth: width => set({ secondaryPaneWidth: width }),
    setActivePane: pane => {
      if (pane.type === null) {
        // Applies the initial state when null
        set({
          activePane: get().activePaneDefaultState
        });
      } else {
        set({ activePane: pane });
      }
    }
  };
};

const appStateSliceInitialState = {
  isDeploying: false
};

const appStateSlice: StateCreator<AppStateSlice> = set => {
  return {
    ...appStateSliceInitialState,
    setIsDeploying: isDeploying => set({ isDeploying })
  };
};

export const useStudioAPIStore = create<StudioAPIGlobalStore>((...args) => ({
  ...collapsedStateSlice(...args),
  ...toolbarStateSlice(...args),
  ...highlightingStatesSlice(...args),
  ...propertiesPaneFormSlice(...args),
  ...layoutStateSlice(...args),
  ...appStateSlice(...args)
}));
